import AllasPlusLogo from '@/allas/public/image/allas-plus-logo-rounded.svg';
import { Icon } from '@/components/Icon';
import { Link } from '@/components/Link';
import { StandaloneArticlePaywall, type PaywallTracking } from 'base/components/ArticlePaywall';
import useUserAuth from '@/hooks/useUserAuth';
import { useFrisbiiPayment } from '@/hooks/useFrisbiiPayment';
import { useEffect, useMemo, useState } from 'react';
import { StandaloneSuccessfulPaymentProps } from 'base/components/ArticlePaywall/SuccessfulPayment';
import { Button } from '@/components/Button';
import { useRouter } from 'next/router';
import NextImage from 'next/image';
import MastercardLogo from 'base/public/image/cards/ma_symbol_opt_45_2x.png';
import VisaLogo from 'base/public/image/cards/Visa_Brandmark_Blue_RGB_2021.png';
import { disableTopAds } from '@/utils/adunit';

const paywallTracking: PaywallTracking = {
  promotion_id: 'paywall_article_allas',
  promotion_name: 'Allas Plus',
  currency: 'SEK',
  value: 29,
  items: [
    {
      item_id: 'allas_plus_monthly',
      item_name: 'Allas Plus',
      item_category: 'subscription',
      price: 29,
      quantity: 1,
    },
  ],
};

export const AllasStandaloneArticlePaywall: typeof StandaloneArticlePaywall = () => {
  disableTopAds();

  const { asPath } = useRouter();
  const { logInUrl, isUserLoggedIn } = useUserAuth();
  const { frisbiiSession } = useFrisbiiPayment();
  const [loginUrl, setLoginUrl] = useState<string>(logInUrl);

  useEffect(() => {
    const url = new URL(logInUrl);
    url.searchParams.set('return_to', asPath);
    setLoginUrl(url.href);
  }, [asPath]);

  const postPaymentProps = useMemo(() => {
    const purchaseCompleted = frisbiiSession?.purchaseCompleted;

    if (!isUserLoggedIn && !purchaseCompleted) {
      return {
        headline: 'Logga in för att fortsätta',
        steps: [],
        content: (
          <>
            <p className="mb-4 text-body-md md:mb-5 md:text-body-lg">
              Ett konto med den angivna e-postadressen finns redan. Logga in för att komma vidare
            </p>
            <Button content={'Logga in'} href={loginUrl} options={{ className: 'mb-6' }} />
          </>
        ),
      } as StandaloneSuccessfulPaymentProps;
    }
  }, [frisbiiSession?.purchaseCompleted, isUserLoggedIn, loginUrl]);

  return (
    <StandaloneArticlePaywall
      paymentMethod="frisbii"
      logo={<AllasPlusLogo />}
      tracking={paywallTracking}
      frisbiiPayment={{
        button: { content: 'Gå till betalning' },
        caption: (
          <>
            <NextImage src={MastercardLogo} alt="Mastercard" width={32} height={20} />
            <NextImage src={VisaLogo} alt="Visa" width={32} height={20} />
          </>
        ),
      }}
      headline="Vill du läsa hela artikeln?"
      description={`Starta din prenumeration på Allas Plus redan idag- just nu för bara 29 kr/månad.`}
      features={[
        <>
          <Icon name="check" options={{ className: 'flex-shrink-0' }} />
          Tillsvidareprenumeration utan bindningstid (säg upp när du vill)
        </>,
        <>
          <Icon name="check" options={{ className: 'flex-shrink-0' }} />
          Obegränsad tillgång till alla artiklar på allas.se
        </>,
        <>
          <Icon name="check" options={{ className: 'flex-shrink-0' }} />
          Exklusivt innehåll på djupgående intervjuer, endast för Plus-prenumeranter.
        </>,
      ]}
      form={{
        button: { content: 'Gå till betalning' },
        caption: (
          <>
            Redan prenumerant? <br className="md:hidden" />
            <Link href={loginUrl} content="Logga in på ditt konto här" />
            <span className="mt-3 flex items-center justify-center gap-2">
              <NextImage src={MastercardLogo} alt="Mastercard" width={32} height={20} />
              <NextImage src={VisaLogo} alt="Visa" width={32} height={20} />
            </span>
          </>
        ),
        policy: (
          <>
            Genom att köpa en Plus-prenumeration skapas automatiskt ett konto åt dig. Dina uppgifter hanteras säkert
            enligt{' '}
            <Link
              content={'Aller Medias personuppgiftspolicy'}
              target="_blank"
              href="https://www.aller.se/integritetspolicy"
            />
            . Kontot är kostnadsfritt.
          </>
        ),
      }}
      successfulPayment={{
        ...postPaymentProps,
        contactInfo: (
          <>
            Har du frågor eller behöver hjälp med din prenumeration, kontakta oss på{' '}
            <Link href="mailto:kundservice@aller.se" content="kundservice@aller.se" />.
          </>
        ),
      }}
    />
  );
};
